import React, { useState, useEffect, useRef } from "react";
import "./ExamSystem.css";
import questionsData from "../Utilities/questionData.json";
import InstructionsModal from "../Components/InstructionsModal/InstructionsModal";
import ShowResult from "../Components/ShowResult/ShowResult";
import ReviewScreen from "../Components/ReviewScreen/ReviewScreen";
import axiosInstance from "../Config/axiosConfig";

const ExamSystem = () => {
  // Existing states
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSubjectForm, setShowSubjectForm] = useState(true);
  const [classLevel, setClassLevel] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [userId, setUserId] = useState('')

  // New states for enhanced features
  const [showInstructions, setShowInstructions] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [wrongQuestions, setWrongQuestions] = useState([]);
  const [sectionsMap, setSectionsMap] = useState({});
  const [sectionsOrder, setSectionsOrder] = useState([]);
  const [sectionsMeta, setSectionsMeta] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [showSectionComplete, setShowSectionComplete] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  
  // New states for backend submission
  const [subjectId, setSubjectId] = useState(null);
  const [classLevelId, setClassLevelId] = useState(null);
  const [lookupData, setLookupData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  const educationOptions = [
    { label: "Primary", value: "primary", icon: "school-outline" },
    { label: "O-Level", value: "secondary", icon: "library-outline" },
    { label: "A-Level", value: "advanceLevel", icon: "school" },
  ];

  const classLevels = {
    primary: ["Standard I", "Standard II", "Standard III", "Standard IV", "Standard V", "Standard VI", "Standard VII"],
    secondary: ["Form I", "Form II", "Form III", "Form IV"],
    advanceLevel: ["Form V", "Form VI"],
  };

  // Extract available subjects from JSON data
  useEffect(() => {
    const availableSubjects = questionsData.subjects.map(subject => ({
      id: subject.id,
      name: subject.name
    }));
    setSubjects(availableSubjects);
    setDataLoaded(true);
  }, []);

  // Fetch lookup data for ID mapping
  useEffect(() => {
    const fetchLookupData = async () => {
      try {
        const response = await axiosInstance.post("Public/get-lookups");
        const lookups = response.data.data;
        setLookupData(lookups);
        console.log("Lookup data loaded:", lookups);
      } catch (error) {
        console.error("Error fetching lookup data:", error);
      }
    };

    fetchLookupData();
  }, []);

  // Map subject and class level names to IDs when they change
  useEffect(() => {
    if (lookupData && selectedSubject) {
      const foundSubject = lookupData.subjects?.find(
        s => s.name.toLowerCase() === selectedSubject.toLowerCase()
      );
      if (foundSubject) {
        setSubjectId(foundSubject.id);
        console.log("Subject ID mapped:", foundSubject.id, "for:", selectedSubject);
      } else {
        console.warn("Subject not found in lookups:", selectedSubject);
        setSubjectId(null);
      }
    }
  }, [selectedSubject, lookupData]);

  useEffect(() => {
    if (lookupData && classLevel) {
      const foundClass = lookupData.educationClasses?.find(
        c => c.name === classLevel
      );
      if (foundClass) {
        setClassLevelId(foundClass.id);
        console.log("Class Level ID mapped:", foundClass.id, "for:", classLevel);
      } else {
        console.warn("Class level not found in lookups:", classLevel);
        setClassLevelId(null);
      }
    }
  }, [classLevel, lookupData]);

 const handleGenerateQuestions = () => {
  if (!educationLevel || !classLevel || !selectedSubject) {
    alert("Please complete all fields.");
    return;
  }

  // Check if we have IDs for backend submission
  if (lookupData) {
    const foundSubject = lookupData.subjects?.find(
      s => s.name.toLowerCase() === selectedSubject.toLowerCase()
    );
    const foundClass = lookupData.educationClasses?.find(
      c => c.name === classLevel
    );

    if (!foundSubject || !foundClass) {
      alert("Unable to map subject or class level to IDs. Please try again.");
      return;
    }
  }

  setLoading(true);

  // Find ALL matching subjects (not just first) - FIX FOR DUPLICATES
  const matchingSubjects = questionsData.subjects.filter(
    s => s.name.toLowerCase() === selectedSubject.toLowerCase()
  );

  console.log(`Found ${matchingSubjects.length} subjects with name "${selectedSubject}"`);
  matchingSubjects.forEach((s, i) => {
    console.log(`  ${i+1}. ID: ${s.id}, Class levels: ${Object.keys(s.classLevels || {}).join(', ')}`);
  });

  let subjectData = null;
  let classData = null;
  let foundClassKey = null;

  // Try each matching subject to find the right class level
  for (const subj of matchingSubjects) {
    console.log(`Checking subject ID ${subj.id}...`);

    // 1. Try exact match first
    if (subj.classLevels && subj.classLevels[classLevel]) {
      subjectData = subj;
      classData = subj.classLevels[classLevel];
      foundClassKey = classLevel;
      console.log(`✅ Exact match found in subject ID ${subj.id}`);
      break;
    }

    // 2. Try case-insensitive match
    const normalizedClass = classLevel.toLowerCase().trim();
    for (const [key, value] of Object.entries(subj.classLevels || {})) {
      if (key.toLowerCase().trim() === normalizedClass) {
        subjectData = subj;
        classData = value;
        foundClassKey = key;
        console.log(`✅ Case-insensitive match: "${classLevel}" → "${key}"`);
        break;
      }
    }

    if (classData) break;
  }

  if (!classData) {
    console.log("❌ No matching class level found in any matching subject");
    console.log("Available data across all matching subjects:");
    matchingSubjects.forEach(subj => {
      console.log(`  Subject ID ${subj.id}: ${Object.keys(subj.classLevels || {}).join(', ')}`);
    });
    
    // Try to suggest alternatives
    const allClassLevels = new Set();
    matchingSubjects.forEach(subj => {
      Object.keys(subj.classLevels || {}).forEach(level => allClassLevels.add(level));
    });
    
    if (allClassLevels.size > 0) {
      alert(`No questions found for "${classLevel}". Available class levels: ${Array.from(allClassLevels).join(', ')}`);
    } else {
      alert(`No questions found for subject: ${selectedSubject}`);
    }
    
    setLoading(false);
    return;
  }

  console.log(`✅ Using: Subject "${subjectData.name}" (ID: ${subjectData.id}), Class: "${foundClassKey}"`);

  try {
    // Extract and organize questions into sections - take 15 questions from each section
    let tempSections = {};
    let order = [];

    const shuffleArray = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    if (classData.examSets && typeof classData.examSets === "object") {
      Object.entries(classData.examSets).forEach(([setName, qArr]) => {
        // Take 15 questions from each section
        const shuffledQuestions = shuffleArray(qArr).slice(0, 15);
        if (shuffledQuestions.length > 0) {
          tempSections[setName] = {
            questions: shuffledQuestions,
            completed: false,
            score: 0
          };
          order.push(setName);
        }
      });
    } else if (Array.isArray(classData)) {
      // If no sections, create one main section with 45 questions
      const name = "Main Section";
      const shuffledQuestions = shuffleArray(classData).slice(0, 45);
      if (shuffledQuestions.length > 0) {
        tempSections[name] = {
          questions: shuffledQuestions,
          completed: false,
          score: 0
        };
        order.push(name);
      }
    } else {
      // Check if classData is an object with sections directly (not in examSets)
      Object.entries(classData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          const shuffledQuestions = shuffleArray(value).slice(0, 15);
          if (shuffledQuestions.length > 0) {
            tempSections[key] = {
              questions: shuffledQuestions,
              completed: false,
              score: 0
            };
            order.push(key);
          }
        }
      });
    }

    // Check if we have any questions
    if (order.length === 0) {
      alert(`No questions found for ${selectedSubject} - ${foundClassKey}.`);
      setLoading(false);
      return;
    }

    // Build flattenedQuestions and sectionsMeta
    const flat = [];
    const meta = [];
    let runningIndex = 0;
    order.forEach(sectionName => {
      const qArr = tempSections[sectionName]?.questions || [];
      if (qArr.length > 0) {
        meta.push({
          name: sectionName,
          startIndex: runningIndex,
          length: qArr.length
        });
        qArr.forEach(q => flat.push(q));
        runningIndex += qArr.length;
      }
    });

    // Validate we have questions
    if (flat.length === 0) {
      alert("No valid questions found. Please try again.");
      setLoading(false);
      return;
    }

    setSectionsMap(tempSections);
    setSectionsOrder(order);
    setExamQuestions(flat);
    setSectionsMeta(meta);
    setCurrentSectionIndex(0);
    setCurrentQuestion(0);
    setSelectedOptions({});
    
    // Show instructions instead of going directly to exam
    setShowInstructions(true);
    setShowSubjectForm(false);
    
    console.log(`✅ Loaded ${flat.length} questions across ${order.length} sections`);
    
  } catch (error) {
    console.error("Error loading questions:", error);
    alert("Error loading questions. Please try again.");
  } finally {
    setLoading(false);
  }
};

  const handleStartExam = () => {
    setShowInstructions(false);
    setExamStarted(true);
    startTimeRef.current = Date.now();
    
    // Start timer
    timerRef.current = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
  };

  // Timer cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Check if current question is the last question of current section
  const isLastQuestionOfSection = () => {
    const currentSectionMeta = sectionsMeta[currentSectionIndex];
    if (!currentSectionMeta) return false;
    
    const lastQuestionOfSection = currentSectionMeta.startIndex + currentSectionMeta.length - 1;
    return currentQuestion === lastQuestionOfSection;
  };

  // Option selection with auto-advance
  const handleOptionSelect = (optionIndex) => {
    const newSelectedOptions = {
      ...selectedOptions,
      [currentQuestion]: optionIndex,
    };
    setSelectedOptions(newSelectedOptions);

    // Auto-advance to next question after selection
    if (autoAdvance) {
      setTimeout(() => {
        // Check if this is the last question of the current section
        if (isLastQuestionOfSection()) {
          handleSectionComplete();
        } else if (currentQuestion < examQuestions.length - 1) {
          setCurrentQuestion(prev => prev + 1);
        } else {
          handleSectionComplete();
        }
      }, 300); // Small delay for better UX
    }
  };

  const handleNext = () => {
    // Check if this is the last question of the current section
    if (isLastQuestionOfSection()) {
      handleSectionComplete();
    } else if (currentQuestion < examQuestions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      handleSectionComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    // Check if this is the last question of the current section
    if (isLastQuestionOfSection()) {
      handleSectionComplete();
    } else if (currentQuestion < examQuestions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      handleSectionComplete();
    }
  };

  const calculateSectionScoreByIndex = (sectionIndex) => {
    const meta = sectionsMeta[sectionIndex];
    if (!meta) return 0;
    let sectionScore = 0;
    for (let i = 0; i < meta.length; i++) {
      const globalIndex = meta.startIndex + i;
      const q = examQuestions[globalIndex];
      if (q && selectedOptions[globalIndex] !== undefined && selectedOptions[globalIndex] === q.correctAnswer) {
        sectionScore++;
      }
    }
    return sectionScore;
  };

  const handleSectionComplete = () => {
    const sectionScore = calculateSectionScoreByIndex(currentSectionIndex);
    const sectionName = sectionsOrder[currentSectionIndex];
    setSectionsMap(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        completed: true,
        score: sectionScore
      }
    }));

    setShowSectionComplete(true);
  };

  const proceedToNextSection = () => {
    const nextIndex = currentSectionIndex + 1;
    setShowSectionComplete(false);

    if (nextIndex < sectionsOrder.length) {
      setCurrentSectionIndex(nextIndex);
      setCurrentQuestion(sectionsMeta[nextIndex].startIndex);
    } else {
      handleFinalSubmit();
    }
  };

  // Replace the submitToBackend function with this:

const submitToBackend = async (totalScore, totalTimeUsedInSeconds) => {
  setSubmitting(true);
  try {
    // Get user data from localStorage
    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem('deviceId') || 'web-browser';
    const storedUserIp = localStorage.getItem('userIp') || '127.0.0.1';
    
    // Get user ID directly from userInfo
    let userIdToUse = '';
    const userInfoString = localStorage.getItem("userInfo");
    
    if (userInfoString) {
      try {
        const userInfo = JSON.parse(userInfoString);
        userIdToUse = userInfo.userId || userInfo.id || '';
      } catch (parseError) {
        console.error("Error parsing userInfo:", parseError);
      }
    }

    // If no user ID found, use a fallback or return
    if (!userIdToUse) {
      console.warn("No user ID found. Skipping backend submission.");
      return;
    }

    if (!accessToken) {
      console.warn("No access token found. Skipping backend submission.");
      return;
    }

    // Check if we have IDs for subject and class level
    if (!subjectId || !classLevelId) {
      console.warn("Missing subject or class level ID:", { subjectId, classLevelId });
      // Try to find IDs from lookupData as fallback
      if (lookupData) {
        const foundSubject = lookupData.subjects?.find(
          s => s.name.toLowerCase() === selectedSubject.toLowerCase()
        );
        const foundClass = lookupData.educationClasses?.find(
          c => c.name === classLevel
        );
        
        if (!foundSubject || !foundClass) {
          console.warn("Cannot find subject or class IDs in lookup data");
          return;
        }
      } else {
        return;
      }
    }

    // Prepare sections data for backend
    const sectionsPayload = sectionsMeta.map((meta, index) => ({
      name: sectionsOrder[index] || `Section ${index + 1}`,
      instructions: "",
      score: calculateSectionScoreByIndex(index),
      questions: examQuestions
        .slice(meta.startIndex, meta.startIndex + meta.length)
        .map((q, i) => {
          const globalIndex = meta.startIndex + i;
          return {
            id: q?.id || globalIndex + 1,
            question: q?.question || "",
            options: q?.options || [],
            correctAnswer: q?.correctAnswer ?? 0,
            selectedAnswer: selectedOptions[globalIndex] ?? null,
            isCorrect: selectedOptions[globalIndex] === q?.correctAnswer
          };
        })
    }));

    // Prepare the complete payload according to the API specification
    const payload = {
      subject: subjectId,
      classLevel: classLevelId,
      userId: userIdToUse,
      totalQuestions: examQuestions.length,
      totalScore: totalScore,
      timeUsed: totalTimeUsedInSeconds,
      assessmentTypeId: 2, //  2 is for Test type
      description: `${classLevel} ${selectedSubject} Exam`,
      instructions: "Complete all questions within the time limit.",
      year: new Date().getFullYear(),
      paperIndexNo: 1,
      paperNo: "P1",
      award: "",
      sections: sectionsPayload
    };

    console.log("📤 Submitting exam results to backend...");
    console.log("📦 Payload:", JSON.stringify(payload, null, 2));
    console.log("🔑 Headers:", {
      Authorization: accessToken ? `Bearer ${accessToken.substring(0, 20)}...` : 'None',
      UserDevice: storedDeviceId,
      UserIPAddress: storedUserIp
    });

    const response = await axiosInstance.post(
      "/Assessment/save-assessment-questions-and-results",
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'UserDevice': storedDeviceId,
          'UserIPAddress': storedUserIp,
        },
      }
    );

    console.log("✅ Backend response:", response.data);
    if (response.data.code === 200) {
      console.log("✅ Exam results successfully saved to backend");
    } else {
      console.warn("⚠️ Backend response indicates an issue:", response.data.message);
    }

  } catch (error) {
    console.error("❌ Error submitting to backend:", error);
    if (error.response) {
      console.error("Status:", error.response.status);
      console.error("Response data:", error.response.data);
      console.error("Request payload:", error.config?.data);
    }
  } finally {
    setSubmitting(false);
  }
};

// Also fix the handleFinalSubmit to ensure it calls submitToBackend properly:
const handleFinalSubmit = async () => {
  // Stop timer
  if (timerRef.current) {
    clearInterval(timerRef.current);
  }

  // Calculate score
  let correctAnswers = 0;
  const wrongOnes = [];

  examQuestions.forEach((q, i) => {
    if (q && selectedOptions[i] === q.correctAnswer) {
      correctAnswers++;
    } else if (q) {
      wrongOnes.push({
        ...q,
        globalIndex: i,
        selected: selectedOptions[i],
        userAnswer: selectedOptions[i] !== undefined ? q.options[selectedOptions[i]] : "Not attempted"
      });
    }
  });

  setScore(correctAnswers);
  setWrongQuestions(wrongOnes);
  
  // Calculate time used
  const endTime = Date.now();
  const totalTimeUsedInSeconds = startTimeRef.current
    ? Math.floor((endTime - startTimeRef.current) / 1000)
    : 0;

  // Update section scores
  const updatedSections = { ...sectionsMap };
  sectionsMeta.forEach((meta, index) => {
    const sectionName = sectionsOrder[index];
    updatedSections[sectionName] = {
      ...updatedSections[sectionName],
      score: calculateSectionScoreByIndex(index),
      completed: true
    };
  });
  setSectionsMap(updatedSections);

  // Submit to backend FIRST, then show results
  try {
    await submitToBackend(correctAnswers, totalTimeUsedInSeconds);
  } catch (error) {
    console.error("Failed to submit to backend:", error);
    // Still show results even if backend submission fails
  }

  // Show results to user
  setShowResults(true); 
  setExamStarted(false);
};

  const handleReview = () => {
    setShowResults(false);
    setShowReview(true);
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setSelectedOptions({});
    setShowResults(false);
    setShowReview(false);
    setShowSubjectForm(true);
    setScore(0);
    setTimeSpent(0);
    setSelectedSubject("");
    setClassLevel("");
    setEducationLevel("");
    setExamStarted(false);
    setSubjectId(null);
    setClassLevelId(null);
  };

  const handleBackToResults = () => {
    setShowReview(false);
    setShowResults(true);
  };

  const handleBackToHome = () => {
    setShowResults(false);
    setShowReview(false);
    setShowSubjectForm(true);
    setSelectedSubject("");
    setClassLevel("");
    setEducationLevel("");
    setScore(0);
    setTimeSpent(0);
    setSubjectId(null);
    setClassLevelId(null);
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600).toString().padStart(2, "0");
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
    const sec = s % 60;
    return `${h}:${m}:${sec.toString().padStart(2, "0")}`;
  };

  // Instructions based on subject
  const englishInstructions = [
    "General Instructions",
    "TIME", 
    "Begin Exam",
    "This exam consists of multiple choice questions.",  
    "Answer all questions carefully.",
    "Time is limited. Focus and avoid distractions.",
    "Each question has only one correct answer.",
    "Do not close the app during the exam.",
    "Click 'Begin Exam' to get started."
  ];

  const kiswahiliInstructions = [
    "Maelekezo kwa ujumla",
    "MUDA",
    "Anza Mtihani",
    "Mtihani huu unajumuisha maswali ya kuchagua",
    "Jibu maswali yote kwa uangalifu.",
    "Muda ni mdogo. Fanya kwa utulivu",
    "Kila swali lina jibu moja tu sahihi.",
    "Usifunge programu wakati wa mtihani.",
    "Bofya 'Anza Mtihani' kuanza."
  ];

  // Render different screens based on state
  if (showReview) {
    return (
      <ReviewScreen
        wrongQuestions={wrongQuestions}
        subject={selectedSubject}
        classLevel={classLevel}
        onBack={handleBackToResults}
      />
    );
  }

  if (showResults) {
    return (
      <ShowResult
        score={score}
        totalQuestions={examQuestions.length}
        questions={examQuestions}
        selectedOptions={selectedOptions}
        timeUsed={timeSpent}
        onReview={handleReview}
        onRetry={handleRetry}
        onHome={handleBackToHome}
        sectionsMeta={sectionsMeta}
        sectionsOrder={sectionsOrder}
        submitting={submitting}
      />
    );
  }

  if (showInstructions) {
    return (
      <InstructionsModal
        visible={showInstructions}
        onClose={() => {
          setShowInstructions(false);
          setShowSubjectForm(true);
        }}
        onStart={handleStartExam}
        examDuration={examQuestions.length}
        subject={selectedSubject}
        instructions={
          selectedSubject.toLowerCase() === "kiswahili"
            ? kiswahiliInstructions
            : englishInstructions
        }
      />
    );
  }

  // Subject and Class Level Form
  if (showSubjectForm) {
    return (
      <div className="exam-system">
        <div className="exam-container">
          <div className="subject-form">
            <h2>🎓 Create Your Exam</h2>
            <p>Enter your class level and subject to generate questions</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleGenerateQuestions();
            }}>
              <div className="form-group">
                <label htmlFor="educationLevel">Education Level:</label>
                <select
                  id="educationLevel"
                  value={educationLevel}
                  onChange={(e) => {
                    setEducationLevel(e.target.value);
                    setClassLevel("");
                    setClassLevelId(null);
                  }}
                  required
                >
                  <option value="">Select your education level</option>
                  {educationOptions.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
                
              {educationLevel && (
                <div className="form-group">
                  <label htmlFor="classLevel">Class Level:</label>
                  <select
                    id="classLevel"
                    value={classLevel}
                    onChange={(e) => setClassLevel(e.target.value)}
                    required
                  >
                    <option value="">Select your class level</option>
                    {classLevels[educationLevel].map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="subject">Subject:</label>
                <select
                  id="subject"
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  required
                >
                  <option value="">Select your subject</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <button 
                type="submit" 
                className="generate-btn"
                disabled={loading || !lookupData || !dataLoaded}
              >
                {loading ? "Generating Questions..." : 
                 !lookupData || !dataLoaded ? "Loading..." : "Generate Exam Questions"}
              </button>
              
              {(!lookupData || !dataLoaded) && (
                <p className="loading-text">Loading data...</p>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="exam-system">
        <div className="exam-container">
          <h2>🧠 Generating your {classLevel} {selectedSubject} exam questions...</h2>
        </div>
      </div>
    );
  }

  // Section Complete Modal
  if (showSectionComplete) {
    const currentSectionName = sectionsOrder[currentSectionIndex];
    const currentSectionScore = calculateSectionScoreByIndex(currentSectionIndex);
    const currentSectionLength = sectionsMeta[currentSectionIndex]?.length || 0;
    
    return (
      <div className="section-complete-modal">
        <div className="section-complete-content">
          <h2>{currentSectionName ? `${currentSectionName} Complete!` : `Section Complete!`}</h2>
          <div className="section-score">
            Score: {currentSectionScore} / {currentSectionLength}
          </div>
          <p>
            {currentSectionIndex < sectionsOrder.length - 1
              ? "Ready to proceed to the next section?"
              : "All sections completed! Ready to see your results?"}
          </p>
          <button className="proceed-button" onClick={proceedToNextSection}>
            {currentSectionIndex < sectionsOrder.length - 1 ? "Proceed to Next Section" : "View Results"}
          </button>
        </div>
      </div>
    );
  }

  // Exam Interface - FIXED: Added safety checks
  if (examStarted && examQuestions.length > 0 && currentQuestion < examQuestions.length) {
    const currentQ = examQuestions[currentQuestion];
    
    // Safety check - if currentQ is undefined, reset to first question
    if (!currentQ) {
      console.error("Current question is undefined, resetting to first question");
      setCurrentQuestion(0);
      return (
        <div className="exam-system">
          <div className="exam-container">
            <span>⚠️ Error loading question. Resetting...</span>
          </div>
        </div>
      );
    }

    const progress = ((currentQuestion + 1) / examQuestions.length) * 100;
    
    // Safer way to get current section meta
    const currentSectionMeta = sectionsMeta[currentSectionIndex] || sectionsMeta.find(meta => 
      currentQuestion >= meta.startIndex && currentQuestion < meta.startIndex + meta.length
    );

    const isLastQuestionOfCurrentSection = isLastQuestionOfSection();
    const isLastQuestionOfExam = currentQuestion === examQuestions.length - 1;
    const isQuestionAnswered = selectedOptions[currentQuestion] !== undefined;

    // Calculate current question in section safely
    const currentQuestionInSection = currentSectionMeta 
      ? currentQuestion - currentSectionMeta.startIndex + 1
      : 1;
    const totalQuestionsInSection = currentSectionMeta?.length || 15;

    return (
      <div className="exam-system">
        <div className="exam-container">
          <div className="exam-header">
            <h3>{classLevel} - {selectedSubject}</h3>
            <div className="timer">Time: {formatTime(timeSpent)}</div>
          </div>

          {/* Section Progress */}
          {sectionsOrder.length > 1 && (
            <div className="section-indicator">
              <div className="current-section">
                {currentSectionMeta?.name || "Section"} 
                {currentSectionMeta && (
                  <span className="section-question-counter">
                    ({currentQuestionInSection} of {totalQuestionsInSection})
                  </span>
                )}
              </div>
              <div className="section-progress">
                {sectionsOrder.map((section, index) => (
                  <div key={section} className="section-dot-container">
                    <div
                      className={`section-dot ${
                        sectionsMap[section]?.completed ? 'completed' : ''
                      } ${currentSectionIndex === index ? 'active' : ''}`}
                    />
                    {index < sectionsOrder.length - 1 && <div className="section-line" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="exam-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="progress-text">
              <span>
                Question {currentQuestion + 1} of {examQuestions.length}
              </span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
          </div>

          <div className="exam-content">
            <h2>{currentQ.question}</h2>
            <div className="options-grid">
              {currentQ.options && currentQ.options.map((opt, i) => (
                <div
                  key={i}
                  className={`option-card ${
                    selectedOptions[currentQuestion] === i ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(i)}
                >
                  <strong>{String.fromCharCode(65 + i)}.</strong> {opt}
                </div>
              ))}
            </div>

            <div className="navigation-buttons">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="nav-btn btn-prev"
              >
                ← Previous
              </button>

              {isLastQuestionOfExam ? (
                <button
                  onClick={handleFinalSubmit}
                  className="nav-btn btn-submit"
                >
                  Submit Exam ✓
                </button>
              ) : (
                <button
                  onClick={isQuestionAnswered ? handleNext : handleSkip}
                  className={`nav-btn ${isQuestionAnswered ? 'btn-next' : 'btn-skip'} ${
                    isLastQuestionOfCurrentSection ? 'btn-section-complete' : ''
                  }`}
                >
                  {isLastQuestionOfCurrentSection 
                    ? (isQuestionAnswered ? 'Complete Section →' : 'Complete Section →')
                    : (isQuestionAnswered ? 'Next →' : 'Skip →')
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="exam-system">
      <div className="exam-container">
        <span>⚠️ No questions generated or error occurred. Please try again.</span>
        <button onClick={() => setShowSubjectForm(true)} className="restart-btn">
          Back to Subject Selection
        </button>
      </div>
    </div>
  );
};

export default ExamSystem;